import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ExternalLink, Clock, FileText, Video, Link as LinkIcon, Loader2, BookMarked } from 'lucide-react';
import { curiosityAPI } from '../services/curiosityAPI';
import { trackContentEvent } from '@/analytics/analytics';

// Mock data generator
const generateMockTopic = (topicId) => {
  return {
    id: topicId,
    title: 'Advanced Problem Solving Techniques',
    description: 'Explore advanced techniques and strategies for solving complex problems. This topic covers multiple approaches and real-world applications.',
    thumbnail_url: null, // Will use getContentThumbnail fallback
    subject: {
      id: 1,
      subject_name: 'Mathematics',
    },
    chapter: {
      id: 1,
      chapter_name: 'Chapter 1: Fundamentals',
    },
    resources: [
      {
        id: 1,
        title: 'Problem Set 1: Basic Techniques',
        description: 'A collection of problems to practice basic problem-solving techniques.',
        resource_type: 'article',
        url: 'https://example.com/resource/1',
        thumbnail_url: null, // Will use getContentThumbnail fallback
        duration_minutes: 15,
      },
      {
        id: 2,
        title: 'Video Tutorial: Step-by-Step Solutions',
        description: 'Watch detailed video explanations of complex problems.',
        resource_type: 'video',
        url: 'https://example.com/resource/2',
        thumbnail_url: null, // Will use getContentThumbnail fallback
        duration_minutes: 20,
      },
      {
        id: 3,
        title: 'Interactive Practice Quiz',
        description: 'Test your understanding with this interactive quiz.',
        resource_type: 'interactive',
        url: 'https://example.com/resource/3',
        thumbnail_url: null, // Will use getContentThumbnail fallback
        duration_minutes: 10,
      },
      {
        id: 4,
        title: 'Case Study: Real-World Applications',
        description: 'Learn how these techniques are applied in real-world scenarios.',
        resource_type: 'article',
        url: 'https://example.com/resource/4',
        thumbnail_url: null, // Will use getContentThumbnail fallback
        duration_minutes: 25,
      },
    ],
  };
};

/**
 * Topic Detail View
 * Path: /curiosity/topic/{topicId}
 * 
 * Features:
 * - Topic title and description header
 * - Thumbnail banner
 * - Grid of resources
 * - Each resource card:
 *   - Thumbnail (if available)
 *   - Title
 *   - Resource type badge
 *   - Duration (if applicable)
 *   - Click → Open resource URL
 */
export default function TopicDetail() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState(null);
  const [resources, setResources] = useState([]);
  const timeSpentIntervalRef = useRef(null);

  useEffect(() => {
    if (topicId) {
      loadTopic();
    }
    // Start periodic page time_spent heartbeat
    try {
      if (timeSpentIntervalRef.current) clearInterval(timeSpentIntervalRef.current);
      timeSpentIntervalRef.current = setInterval(() => {
        try {
          trackContentEvent('page_time_spent', {
            page: 'topic_detail',
            topic_id: topicId ? parseInt(topicId) : null,
            time_spent_seconds: 15,
          });
        } catch (e) {}
      }, 15000);
    } catch (e) {}

    return () => {
      try { if (timeSpentIntervalRef.current) clearInterval(timeSpentIntervalRef.current); } catch (e) {}
    };
  }, [topicId]);

  const loadTopic = async () => {
    try {
      setLoading(true);
      
      const response = await curiosityAPI.getTopic(topicId);
      setTopic(response);
      setResources(response.resources || []);
    } catch (error) {
      console.error('Failed to load topic:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResourceClick = async (resource) => {
    if (resource.url) {
      // Track interaction (non-blocking)
      curiosityAPI.recordInteraction({
        topic_id: parseInt(topicId),
        interaction_type: 'clicked',
      }).catch(err => console.warn('Failed to record interaction:', err));
      
      // Open in new tab
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    }
  };

  const getResourceIcon = (resourceType) => {
    switch (resourceType) {
      case 'video':
        return <Video className="h-5 w-5" />;
      case 'article':
        return <FileText className="h-5 w-5" />;
      case 'pdf':
        return <FileText className="h-5 w-5" />;
      case 'external_link':
        return <LinkIcon className="h-5 w-5" />;
      default:
        return <ExternalLink className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--green-bright))]" />
        </div>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
        <div className="max-w-4xl mx-auto w-full px-6 py-12">
          <div className="text-center space-y-4">
            <p className="text-muted-foreground text-lg">Topic not found</p>
            <Button 
              onClick={() => navigate(-1)} 
              className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
      <div className="max-w-7xl mx-auto w-full px-6 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="hover:bg-[hsl(var(--card-bg))]"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>

        {/* Topic Header */}
        <div className="space-y-4">
          {topic.thumbnail_url && (
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] overflow-hidden">
              <CardContent className="p-0">
                <div className="w-full h-64 md:h-80 relative">
                  <img
                    src={topic.thumbnail_url}
                    alt={topic.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target).style.display = 'none';
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          )}
          
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <BookMarked className="h-6 w-6 text-[hsl(var(--green-bright))]" />
              <h1 className="text-3xl font-bold text-foreground">{topic.title}</h1>
            </div>
            {topic.description && (
              <p className="text-muted-foreground text-lg max-w-3xl">{topic.description}</p>
            )}
            {(topic.subject || topic.chapter) && (
              <div className="flex items-center gap-2 mt-3">
                {topic.subject && (
                  <Badge variant="outline">{topic.subject.subject_name}</Badge>
                )}
                {topic.chapter && (
                  <Badge variant="secondary">{topic.chapter.chapter_name}</Badge>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Resources Grid */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-1 w-12 bg-gradient-to-r from-[hsl(var(--orange-warm))] to-[hsl(var(--green-bright))] rounded-full"></div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              Resources
              <Badge variant="outline" className="ml-2">
                {resources.length}
              </Badge>
            </h2>
          </div>
          
          {resources.length === 0 ? (
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-12">
                <div className="text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No resources available for this topic</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {resources.map((resource) => (
                <Card
                  key={resource.id}
                  className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] cursor-pointer hover:border-[hsl(var(--primary))] hover:shadow-lg transition-all"
                  onClick={() => handleResourceClick(resource)}
                >
                  <CardContent className="p-0">
                    {resource.thumbnail_url && (
                      <div className="w-full h-40 overflow-hidden rounded-t-lg">
                        <img
                          src={resource.thumbnail_url}
                          alt={resource.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            (e.target).style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start gap-3 mb-2">
                        {getResourceIcon(resource.resource_type)}
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-1 text-foreground">{resource.title}</h3>
                          {resource.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {resource.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[hsl(var(--card-border))]">
                        <Badge variant="outline" className="text-xs">
                          {resource.resource_type}
                        </Badge>
                        {resource.duration_minutes && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>{resource.duration_minutes} min</span>
                          </div>
                        )}
                        <ExternalLink className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

